// Typed wrapper for the document preview — what THIS company's receipt or
// invoice will look like, before a real one exists.
//
// PREVIEW MINTS NOTHING AND BURNS NO NUMBER. That is the whole reason it is its
// own endpoint rather than "generate one and delete it". Document numbers are
// GAPLESS per (company_id, doc_type): a throwaway would either consume
// RCPT-000001 or leave a permanent hole in a series a tax authority may one day
// read, and there is no delete that un-makes a gap. So nothing here is
// persisted, nothing is sequenced, and the operator can hit preview as often as
// they like.
//
// It is not a template gallery either: the render reads the company's REAL
// state — issuer block, printing contacts, default products — so what comes
// back answers "does MY document look right", which an abstract template render
// cannot. Only the amount, currency, customer name and document number are
// invented, and the number is deliberately the literal "PREVIEW" so a
// screenshot of one can never be mistaken for an issued document.
import { apiClient } from "./client";

/**
 * Document kinds the preview can render.
 *
 * Wider than DocumentType in ./documents on purpose: payment_confirmation is a
 * registered template go-pim can issue, but the generated-documents listing has
 * no rows of it to show yet. Do not narrow this to match — the server validates
 * against its own registry and 400s on anything else.
 */
export type PreviewDocType = "receipt" | "invoice" | "payment_confirmation";

export const PREVIEW_DOC_TYPE_LABELS: Record<PreviewDocType, string> = {
  receipt: "Receipt",
  invoice: "Invoice",
  payment_confirmation: "Payment confirmation",
};

export const PREVIEW_DOC_TYPES: PreviewDocType[] = [
  "receipt",
  "invoice",
  "payment_confirmation",
];

/**
 * Every parameter is optional and every default is the SERVER'S, not this
 * module's — sending nothing is the intended first call, so the operator sees a
 * document immediately instead of a form.
 *
 *   doc_type       the first kind the company's document_mode permits
 *   currency       "INR"
 *   amount_minor   ₹85,000 — chosen to exercise a primary/secondary split, so
 *                  an amount of 0 or less is treated as "unset", not as a
 *                  zero-value document
 *   customer_name  "Sample Customer"
 *
 * Deliberately not mirrored as constants here: duplicating them would let this
 * panel drift into claiming a default the server no longer uses.
 */
export interface PreviewParams {
  doc_type?: PreviewDocType;
  /** Minor units (paise/cents) as bigint — money is bigint end-to-end. */
  amount_minor?: bigint;
  currency?: string;
  customer_name?: string;
}

export interface PreviewResult {
  /**
   * A COMPLETE, SERVER-RENDERED HTML DOCUMENT.
   *
   * Render it into a sandboxed iframe via `srcDoc`, never through
   * dangerouslySetInnerHTML: it is a whole page (its own <style>, its own
   * layout) that would otherwise leak its styles over the panel, and it carries
   * merchant-controlled strings — issuer name, address, product names — so
   * injecting it into this origin's DOM would give a merchant a script foothold
   * in an admin session.
   */
  html: string;
  /** The kind actually rendered — the resolved default when none was asked for. */
  doc_type: PreviewDocType;
  /** Template key used, after fallback to the registered generic. */
  template: string;
  /**
   * Things the operator should know about what they are looking at: an empty
   * issuer, no default products, a document_mode that issues nothing today.
   *
   * SHOW THESE. They are the difference between "the preview looks sparse" and
   * "this company is not configured to issue anything" — which the render alone
   * cannot say, because a preview renders happily either way.
   */
  notes: string[];
}

/** The number stamped on every preview, in place of a real sequence value. */
export const PREVIEW_NUMBER = "PREVIEW";

/**
 * previewDocument — GET /v1/companies/:companyID/preview
 *
 * amount_minor goes on the wire as a decimal STRING. Query serialisers have no
 * agreed handling for bigint, and the one conversion that always compiles —
 * Number(...) — silently truncates past 2^53, which is precisely the failure
 * bigint money exists to prevent. `.toString()` is exact for any magnitude.
 */
export async function previewDocument(
  companyId: string,
  params: PreviewParams = {},
): Promise<PreviewResult> {
  const query: Record<string, string> = {};
  if (params.doc_type) query.doc_type = params.doc_type;
  if (params.amount_minor != null)
    query.amount_minor = params.amount_minor.toString();
  if (params.currency) query.currency = params.currency;
  if (params.customer_name) query.customer_name = params.customer_name;

  const resp = await apiClient.get<PreviewResult>(
    `/v1/companies/${companyId}/preview`,
    { params: query },
  );
  return {
    ...resp.data,
    // Go marshals an empty note slice as `null`, not `[]`, so mapping over it
    // straight from the response throws on the common, healthy case — a fully
    // configured company with nothing to warn about.
    notes: resp.data.notes ?? [],
  };
}
